/**
 * Cookie utility functions for cross-domain cookie consent sharing
 * Uses domain=.shiptanbul.com to share cookies between shiptanbul.com and app.shiptanbul.com
 */

const COOKIE_DOMAIN = '.shiptanbul.com'
const COOKIE_PATH = '/'
const COOKIE_MAX_AGE = 31536000 // 1 year in seconds
const COOKIE_SAME_SITE = 'Lax'

interface CookieOptions {
  maxAge?: number
  domain?: string
  path?: string
  sameSite?: 'Strict' | 'Lax' | 'None'
  secure?: boolean
}

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null

  const nameEQ = name + '='
  const ca = document.cookie.split(';')

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }
  }
  return null
}

/**
 * Set a cookie with domain sharing enabled
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  if (typeof document === 'undefined') return

  const {
    maxAge = COOKIE_MAX_AGE,
    domain = COOKIE_DOMAIN,
    path = COOKIE_PATH,
    sameSite = COOKIE_SAME_SITE,
    secure = window.location.protocol === 'https:',
  } = options

  let cookieString = `${name}=${encodeURIComponent(value)}`

  if (maxAge) {
    cookieString += `; max-age=${maxAge}`
  }

  if (domain) {
    cookieString += `; domain=${domain}`
  }

  if (path) {
    cookieString += `; path=${path}`
  }

  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`
  }

  if (secure) {
    cookieString += '; Secure'
  }

  document.cookie = cookieString
}

/**
 * Delete a cookie
 */
export const deleteCookie = (name: string, options: CookieOptions = {}): void => {
  if (typeof document === 'undefined') return

  const { domain = COOKIE_DOMAIN, path = COOKIE_PATH } = options

  // Set cookie with expiration in the past to delete it
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=${path};`
}
